// LeadsMorningBriefBanner — Section 1 of the Leads AI Command Center.
//
// Renders at the very top of LeadsView. Data from
// /api/leads/morning-brief. One-look answer to "what should I do?"
// — hot counts, potential revenue, top action, single CTA button.
//
// Fail-soft: hides itself if the endpoint errors or returns
// empty. Never shows fake stats.

import React from 'react';
import { useLeadIntelligence } from './LeadIntelligenceContext';

function fmt$(cents) {
  if (!Number.isFinite(cents) || cents === 0) return '$0';
  return `$${(cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function LeadsMorningBriefBanner({ currentUser, onOpenLead, onStartPowerDial }) {
  const state = useLeadIntelligence();
  if (state.loading || state.error || !state.data) return null;
  const d = state.data;
  const nothing = d.counts.total_active === 0;
  if (nothing) return null;   // silence beats "you have 0 hot leads"

  const firstName = String(currentUser?.email || '').split('@')[0].split('.')[0];
  const cap = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : '';

  return (
    <div style={{
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      border: '1px solid #334155',
      borderRadius: 14,
      padding: 20,
      color: '#e2e8f0',
      marginBottom: 16,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <div style={{
          fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase',
          color: '#22d3ee', fontWeight: 800,
        }}>🤖 AI Lead Command Center</div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>
          {greetingByHour()}{cap ? `, ${cap}` : ''}.
        </div>
      </div>

      {/* Opportunity row */}
      <div style={{
        display: 'grid', gap: 12, marginBottom: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      }}>
        <Stat icon="🔥" label="Hot leads"        value={d.counts.hot}                tone={d.counts.hot > 0 ? '#f97316' : '#94a3b8'}/>
        <Stat icon="📅" label="Follow-ups due"   value={d.follow_ups_due}            tone={d.follow_ups_due > 0 ? '#f59e0b' : '#94a3b8'}/>
        <Stat icon="⚠️" label="At risk"          value={d.counts.at_risk}            tone={d.counts.at_risk > 0 ? '#ef4444' : '#94a3b8'}/>
        <Stat icon="💰" label="Potential today"  value={fmt$(d.potential_revenue_cents)} tone="#22c55e"/>
        <Stat icon="📞" label="Recommended calls" value={d.recommended_calls}         tone="#22d3ee"/>
      </div>

      {/* AI Recommendation strip */}
      {d.top_action && (
        <div style={{
          background: 'rgba(34, 211, 238, 0.08)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderRadius: 10,
          padding: 14,
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#22d3ee', fontWeight: 800, marginBottom: 4 }}>
              AI Recommendation
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#f8fafc' }}>
              {d.top_action.verb} <span style={{ color: '#22d3ee' }}>{d.top_action.lead_name}</span> first.
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
              {d.top_action.reason} · {fmt$(d.top_action.expected_commission_cents)} expected
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => onOpenLead?.(d.top_action.lead_id)}
              style={{
                background: 'transparent', border: '1px solid #334155', color: '#e2e8f0',
                padding: '9px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Open lead
            </button>
            <button
              onClick={() => onStartPowerDial?.()}
              style={{
                background: '#22d3ee', border: 'none', color: '#0f172a',
                padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer',
              }}
            >
              ▶ Start AI Power Dial
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, tone }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 10,
      padding: '10px 12px',
    }}>
      <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 700, marginBottom: 3 }}>
        {icon} {label}
      </div>
      <div style={{
        fontSize: 20, fontWeight: 900, color: tone,
        fontFamily: 'JetBrains Mono, monospace', fontVariantNumeric: 'tabular-nums',
      }}>{value}</div>
    </div>
  );
}
